import type { TextosPortal } from './pt';

/** Portal do Cliente (Onda 1): textos do app "Malgo" em inglês. */
export const portalEn: TextosPortal = {
  app: { nome: 'Malgo', subtitulo: 'Client portal', carregando: 'Loading the portal', titulo: 'Your company and Malgo, in one place.', texto: 'Invoices, documents and a conversation with the team, on your phone or computer.' },
  faixa: { sandbox: 'Sandbox: test environment', verComo: 'Malgo team view: read only, recorded in the audit log' },
  menu: { hoje: 'Today', saida: 'Your access', financeiro: 'Billing', relatorios: 'Reports', documentos: 'Documents', mensagens: 'Messages', perfil: 'Profile', empresas: 'Switch company', sair: 'Sign out', principal: 'Portal navigation', conta: 'Account and company' },
  entrar: {
    passkey: 'Sign in with a passkey (Face ID, fingerprint)',
    passkeyErro: 'Passkey not recognized. Sign in with the e-mail code.',
    titulo: 'Sign in to the portal',
    texto: 'Type your e-mail. We send a code and a sign-in link, no password needed.',
    email: 'E-mail',
    pedir: 'Get a code',
    enviado: 'If the e-mail has access to the portal, the code arrives in a moment. Check your spam folder too.',
    codigo: '6-digit code',
    confirmar: 'Sign in',
    reenviar: 'Send another code',
    trocarEmail: 'Use another e-mail',
    comSenha: 'Sign in with password',
    semSenha: 'Sign in with an e-mail code',
    senha: 'Password',
    lembrar: 'Remember this device for 30 days',
    codigoInvalido: 'Wrong or expired code. Check the latest e-mail or ask for another one.',
    credenciais: 'Wrong e-mail or password.',
    bloqueado: 'Too many attempts. Wait a few minutes and try again.',
    linkTitulo: 'Sign in with the e-mail link',
    linkTexto: 'Tap the button to sign in. The link works only once, on this device.',
    linkEntrar: 'Sign in',
    linkOutroAparelho: 'This link only works on the device where you asked for it. Type the 6-digit code from the same e-mail.',
    linkInvalido: 'Invalid, used or expired link. Ask for a new code.',
    golpe: 'Malgo never asks for a password or code by message or phone.',
  },
  convite: {
    titulo: 'Invitation to the portal',
    texto: '{{escritorio}} invited you to the portal of {{empresa}}.',
    termo: 'Terms of use',
    aceito: 'I have read and accept the terms of use',
    entrar: 'Accept and sign in',
    invalido: 'Invalid, used or expired invitation. Ask Malgo for a new one.',
    termoObrigatorio: 'Accept the terms of use to sign in.',
  },
  verComo: { titulo: 'Opening the team view', invalido: 'Invalid or expired view link. Create another one on the client record.' },
  termo: { titulo: 'Terms of use updated', texto: 'The portal terms of use changed. Read and accept them to continue.', aceitar: 'I have read and accept', versao: 'Version {{versao}}' },
  saida: {
    titulo: 'Exit access',
    texto: 'Your last contract with the firm has ended. Until {{data}} you can still view your documents and billing history and download your data to take to another firm.',
    depois: 'After that date, access to the portal ends.',
    baixar: 'Download my data',
    baixarTexto: 'A file with your invoices, subscriptions and the list of documents. Each document file downloads in Documents.',
    documentos: 'View documents',
    financeiro: 'View billing history',
    baixado: 'Data downloaded',
  },
  hoje: {
    titulo: 'Today',
    saudacao: 'Hello, {{nome}}',
    tudoEmDia: 'All up to date.',
    tudoEmDiaTexto: 'Nothing depends on you right now. We will let you know when something new arrives.',
    fatura: 'Invoice {{numero}}',
    vencida: 'Overdue since {{data}}',
    vence: 'Due on {{data}}',
    pagar: 'Pay',
    ver: 'View',
    entrega: 'New document',
    assinatura: 'To sign',
    abrir: 'Open',
    mensagem_one: 'New reply from the team',
    mensagem_other: '{{count}} new replies from the team',
    responder: 'Reply',
    lista: 'What depends on you',
  },
  financeiro: {
    titulo: 'Billing',
    abertas: 'Open',
    pagas: 'Paid in the last 12 months',
    semAbertas: 'No open invoices.',
    semPagas: 'No invoices paid in the last 12 months.',
    resumo: '{{ano}} summary',
    faturado: 'Billed this year',
    pago: 'Paid this year',
    aberto: 'Open',
    vencido: 'Overdue',
    estado: { em_aberto: 'Open', parcial: 'Partly paid', pago: 'Paid', vencida: 'Overdue' },
    vencimento: 'Due date',
    emissao: 'Issued',
    competencia: 'Period',
    valor: 'Amount',
    saldo: 'Balance due',
    pagoEm: 'Paid on {{data}}',
    parcela: 'Installment {{parcela}} of {{parcelas}}',
    pix: 'Pix copy and paste',
    qr: 'Pix QR code',
    copiar: 'Copy code',
    copiado: 'Code copied',
    boleto: 'Boleto barcode line',
    copiarLinha: 'Copy barcode line',
    abrirBoleto: 'Open boleto',
    abrirFatura: 'Open the invoice on Asaas',
    semCobranca: 'The payment method of this invoice is still being prepared. Talk to the team if you need to pay now.',
    outraEmpresaTitulo: 'Invoice from another company',
    outraEmpresa: 'This invoice belongs to a company your account cannot access on this portal. Sign in with the email that received the invoice, or talk to the team.',
    pagoAgora: 'Payment confirmed. Thank you!',
    voltar: 'Back to billing',
    ateAgora: 'Updates by itself when the payment arrives.',
    assinaturas: {
      titulo: 'Subscriptions',
      vazio: 'No active subscriptions.',
      pausada: 'Paused',
      ativa: 'Active',
      desde: 'Since {{data}}',
      ate: 'Until {{data}}',
      proximas: 'Next billings',
      semProximas: 'No scheduled billing.',
      avulso: 'One-off billing',
      recorrencia: { mes_one: 'Every month', mes_other: 'Every {{count}} months', semana_one: 'Every week', semana_other: 'Every {{count}} weeks', dia_one: 'Every day', dia_other: 'Every {{count}} days' },
      ajuda: 'To change or cancel a subscription, talk to the team in Messages.',
    },
  },
  relatorios: {
    titulo: 'Reports',
    texto: 'Reports Malgo prepared for your company. Each one always opens with current figures.',
    vazio: 'Malgo has not published any reports for you yet.',
    tipo: { posicao: 'Financial position', extrato: 'Statement for the period' },
    publicado: 'Published on {{data}}',
    voltar: 'Back to reports',
    posicaoEm: 'Open invoices on {{data}}',
    emitidasEntre: 'Invoices issued from {{de}} to {{ate}}',
    pagasEntre: 'Invoices paid from {{de}} to {{ate}}',
    totais: 'Report totals',
    documentos: 'Invoices',
    pago: 'Paid',
    pagoEm: 'Payment',
    numero: 'Invoice',
    atraso: 'Overdue',
    dias_one: '{{count}} day',
    dias_other: '{{count}} days',
    aVencer: 'Not yet due',
    situacao: 'Status',
    tabela: 'Report invoices',
    semFaturas: 'No invoices in the period.',
    truncado: 'Showing the first {{n}} invoices; the totals add up all of them.',
    atualizado: 'Up to date: the figures are from the moment you opened it.',
  },
  assinar: {
    aba: 'To sign',
    vazio: 'No documents for you to sign.',
    situacao: { pendente: 'Waiting for your signature', assinado: 'Signed', recusado: 'Declined', encerrado: 'Closed' },
    versao: 'Version {{versao}}',
    enviado: 'sent on {{data}}',
    ate: 'sign by {{data}}',
    ler: 'Read',
    assinar: 'Sign',
    recusar: 'Decline',
    cancelar: 'Cancel',
    titulo: 'Sign the document',
    explicacao: 'To sign, confirm with the code we sent to your e-mail. The date and time, your IP, the browser and the file code (hash) of this version are recorded.',
    hash: 'File code (SHA-256)',
    codigo: 'E-mail code',
    codigoAjuda: 'Six digits. Ask for a new code if it did not arrive.',
    assinado: 'Document signed',
    recusarTitulo: 'Decline the signature',
    motivo: 'Reason',
    motivoAjuda: 'The Malgo team receives the reason.',
    recusado: 'Signature declined',
  },
  documentos: {
    titulo: 'Documents',
    daMalgo: 'From Malgo',
    enviar: 'Send',
    semEntregas: 'Malgo has not published documents for you yet. When it does, they show up here. If you need to send a document, send it from here.',
    enviarAgora: 'Send a document',
    novo: 'New',
    baixar: 'Download',
    visto: 'Seen on {{data}}',
    publicado: 'Published on {{data}}',
    area: { financeiro: 'Finance', dp: 'Payroll and HR', fiscal: 'Tax', contabil: 'Accounting', societario: 'Corporate', geral: 'General' },
    tipo: { extrato: 'Bank statement', nota_fiscal: 'Invoice (NF)', folha_ponto: 'Timesheet', admissao: 'Hiring', comprovante: 'Receipt', contrato: 'Contract', outro: 'Other' },
    enviarTitulo: 'Send a document to Malgo',
    enviarTexto: 'Take a photo or choose the file: PDF, image, spreadsheet, document or invoice XML, up to 25 MB.',
    arquivo: 'File',
    camera: 'Use the camera',
    escolher: 'Choose file',
    tipoCampo: 'Type',
    areaCampo: 'Area',
    competenciaCampo: 'Period (optional)',
    descricaoCampo: 'Note (optional)',
    mandar: 'Send',
    enviado: 'Document sent. The team was notified.',
    enviados: 'Sent by your company',
    semEnviados: 'Nothing sent yet.',
    recebido: 'Received by Malgo',
    aguardando: 'Waiting for Malgo',
    meu: 'Sent by you',
  },
  mensagens: {
    titulo: 'Messages',
    texto: 'Talk to the Malgo team. The reply arrives here and on your phone.',
    vazia: 'No messages yet. Write the first one.',
    escrever: 'Write your message',
    enviar: 'Send',
    equipe: 'Malgo team',
    voce: 'You',
    estado: { pendente: 'Sending', entregue: 'Delivered', lida: 'Read', falhou: 'Not sent' },
    anexar: 'Attach file',
    anexarAjuda: 'PDF, image, spreadsheet, document or invoice XML, up to 25 MB',
    removerAnexo: 'Remove the file {{nome}}',
    baixarAnexo: 'Download {{nome}}',
    indisponivel: 'Messages through the portal are not turned on at your firm yet.',
  },
  perfil: {
    passkeys: 'Passkey',
    passkeysTexto: 'Sign in with Face ID, fingerprint or Windows Hello on this device, without a code. The e-mail code still works.',
    cadastrarPasskey: 'Sign in without a password on this device (passkey)',
    removerPasskey: 'Remove',
    removerPasskeyTexto: 'To remove the passkey "{{nome}}", confirm it is you.',
    passkeyRemovida: 'Passkey removed.',
    passkeyCadastrada: 'Passkey registered.',
    semPasskey: 'No passkey registered.',
    passkeyUsada: 'Last used: {{data}}',
    titulo: 'Profile',
    nome: 'Name',
    salvarNome: 'Save',
    idioma: 'Language',
    tema: 'Theme',
    temaSistema: 'Device setting',
    temaClaro: 'Light',
    temaEscuro: 'Dark',
    fonte: 'Text size',
    fonteNormal: 'Normal',
    fonteMaior: 'Larger',
    fonteGrande: 'Large',
    fonteMuitoGrande: 'Extra large',
    simples: 'Simple mode',
    simplesAjuda: 'Hides Reports and the company switch from the menu. You can still open everything by link.',
    simplesDesligado: 'Off',
    simplesLigado: 'On',
    avisos: 'Notifications on this device',
    avisosTexto: 'Get a notification when there is a new invoice, document or reply from the team. It never shows amounts or names.',
    ativarAvisos: 'Turn on notifications',
    avisosAtivos: 'Notifications are on for this device',
    desativarAvisos: 'Turn off on this device',
    avisosNegados: 'Notifications are blocked in the browser. Allow them in the device settings.',
    avisosIos: 'On iPhone and iPad, install the app: tap Share and then "Add to Home Screen".',
    avisosSemSuporte: 'This browser does not receive notifications.',
    seguranca: 'Security',
    senha: 'Password (optional)',
    senhaTexto: 'You sign in with the e-mail code. If you prefer, set a password.',
    senhaAtual: 'Current password',
    confirmacaoTexto: 'To change the password, register or remove a passkey or anonymize the account, confirm it is you: type your current password or the code we sent to your e-mail.',
    codigoEmail: 'E-mail code',
    enviarCodigo: 'Send code to my e-mail',
    codigoEnviado: 'We sent a code to your e-mail. It is valid for 10 minutes.',
    confirmarPasskey: 'Confirm it is you',
    senhaNova: 'New password (at least 10 characters)',
    salvarSenha: 'Save password',
    removerSenha: 'Remove password',
    senhaSalva: 'Password saved. Your other sessions were ended.',
    sairTodos: 'Sign out of all other devices',
    sairTodosFeito_one: '{{count}} session ended',
    sairTodosFeito_other: '{{count}} sessions ended',
    dados: 'Your data',
    dadosTexto: 'Download everything the portal keeps about your account, or anonymize the account (you leave every company).',
    exportar: 'Download my data',
    anonimizar: 'Erase my personal data (anonymize)',
    anonimizarTitulo: 'Erase my personal data (anonymize)',
    anonimizarTexto: 'Your name and e-mail leave the portal and you lose access to every company. The documents your company sent stay with it. To confirm, type your e-mail and confirm it is you with your current password or the e-mail code.',
    confirmacao: 'Your e-mail',
    termo: 'Terms of use accepted: version {{versao}} on {{data}}',
    empresa: 'Company',
    papel: 'Your role',
  },
  // U15 F1: help for each screen. Button and tab names come from the screen's own keys ($t), so they never drift apart.
  ajuda: {
    botao: 'Help',
    titulo: 'Help: {{tela}}',
    paraQue: 'What it is for',
    como: 'How to do it',
    erro: 'If something goes wrong',
    deNovo: 'Show again next visit',
    deNovoFeito: 'Done. The help opens again the next time you enter this screen.',
    lerEmVoz: 'Read aloud',
    pararLeitura: 'Stop',
    entendi: 'Got it',
    hoje: {
      para: 'This is what is waiting for you. If there is nothing, you see All up to date.',
      passos: [
        'Look at the list $t(hoje.lista).',
        'On an invoice, tap $t(hoje.pagar).',
        'On a new document or one to sign, tap $t(hoje.abrir).',
        'On a reply from the team, tap $t(hoje.responder).',
      ],
      erro: 'If the list does not show or nothing opens, tell the team that helps you.',
    },
    financeiro: {
      para: 'Here you see your company invoices and how much is left to pay.',
      passos: [
        'Look at the invoices in the list $t(financeiro.abertas).',
        'Tap the invoice you want to pay.',
        'Tap $t(financeiro.copiar) and pay with Pix in your bank app.',
        'If you prefer a boleto, tap $t(financeiro.copiarLinha) or $t(financeiro.abrirBoleto).',
        'When the payment arrives, the invoice leaves $t(financeiro.abertas) on its own.',
      ],
      erro: 'If the payment does not show, tell the team that helps you.',
    },
    mensagens: {
      para: 'Here you talk to the Malgo team. The reply arrives here and on your phone.',
      passos: [
        'Write in the field $t(mensagens.escrever).',
        'To send a file, tap the paperclip ($t(mensagens.anexar)).',
        'Tap $t(mensagens.enviar).',
        'Under the message, see if it was delivered or read.',
      ],
      erro: 'If the message stays as Not sent, tell the team that helps you.',
    },
    documentos: {
      para: 'Here are the documents Malgo published for you and the ones you send.',
      passos: [
        'On the tab $t(documentos.daMalgo), tap $t(documentos.baixar) to keep a document.',
        'If the tab $t(assinar.aba) exists, tap $t(assinar.ler) and then $t(assinar.assinar).',
        'Confirm the signature with the code that arrived in your email.',
        'To send, open the tab $t(documentos.enviar) and tap $t(documentos.camera) or $t(documentos.escolher).',
        'Choose the type and the area, then tap $t(documentos.mandar).',
      ],
      erro: 'If the file is refused or no code arrives, tell the team that helps you.',
    },
    perfil: {
      para: 'Here you manage your account, your notifications and the security of your access.',
      passos: [
        'Change your $t(perfil.nome), $t(perfil.idioma), $t(perfil.tema) and $t(perfil.fonte).',
        'Under $t(perfil.avisos), tap $t(perfil.ativarAvisos) to get alerts on your phone.',
        'To skip the code, tap $t(perfil.cadastrarPasskey).',
        'Under $t(perfil.dados), tap $t(perfil.exportar) to get a copy.',
        'To remove your name and email from the portal, tap $t(perfil.anonimizar).',
      ],
      erro: 'If the confirmation fails, ask for another code or tell the team that helps you.',
    },
  },
  papel: { dono: 'Owner', financeiro: 'Finance', dp: 'Payroll and HR', fiscal: 'Tax', societario: 'Corporate', colaborador: 'Employee' },
  empresas: { titulo: 'Your companies', texto: 'Choose the company you want to see now.', atual: 'Current', escolher: 'View this one' },
  instalar: { titulo: 'Install the Malgo app', texto: 'Keep the portal on your home screen and get notifications.', botao: 'Install', depois: 'Not now' },
  erros: {
    codigos: {
      'portal.passkey_invalida': 'Passkey not recognized. Sign in with the e-mail code.',
      'portal.nao_autenticado': 'Your session ended. Sign in again.',
      'portal.codigo_invalido': 'Wrong or expired code.',
      'portal.credenciais_invalidas': 'Wrong e-mail or password.',
      'portal.bloqueado': 'Too many attempts. Wait a few minutes.',
      'portal.link_outro_aparelho': 'This link only works on the device where you asked for it. Type the e-mail code.',
      'portal.link_invalido': 'Invalid, used or expired link. Ask for a new one.',
      'portal.convite_invalido': 'Invalid or expired invitation.',
      'portal.termo_obrigatorio': 'Accept the terms of use to sign in.',
      'portal.termo_pendente': 'Accept the terms of use to continue.',
      'portal.termo_mudou': 'The terms changed. Read the new version.',
      'portal.somente_leitura': 'Team view: read only.',
      'portal.nao_encontrado': 'Not found.',
      'portal.area_invalida': 'Area outside your access.',
      'portal.senha_atual_incorreta': 'Wrong current password.',
      'portal.confirmacao_incorreta': 'Type your e-mail to confirm.',
      'portal.empresa_nao_encontrada': 'Company not found.',
      'arquivo.tipo_nao_permitido': 'File type not allowed. Send a PDF, image, spreadsheet, document or invoice XML.',
      'arquivo.muito_grande': 'File larger than 25 MB.',
      'portal.envio_limite': 'You reached the upload limit for now (30 per hour and 300 MB per day). Try again later or talk to the team.',
      'portal.confirmacao_obrigatoria': 'Confirm with your current password or with the code we sent to your e-mail.',
      'portal.mensagem_limite': 'You reached the message limit for now (60 per hour). Try again later.',
      'arquivo.vazio': 'Empty file.',
      'dispositivo.invalido': 'Could not turn on notifications on this device.',
    },
  },
};
